// ABOUTME: Redesigned hypothesis view with better hierarchy and user experience
// ABOUTME: Shows verdict prominently, followed by executive summary, then collapsible details

import React from 'react';
import { AnalysisData, ExecutiveSummary } from '@/types/analysis';
import { CollapsibleSection } from '@/components/common/CollapsibleSection';
import { Statistics } from './Statistics';
import { Recommendations } from './Recommendations';
import { SignalAnalysis } from './SignalAnalysis';
import { EvidenceViewer } from './EvidenceViewer';
import { ConditionsForTruth } from './ConditionsForTruth';
import { KeyEvidencePatterns } from './KeyEvidencePatterns';
import { useAuth } from '@/contexts/AuthContext';

interface HypothesisViewProps {
  data: AnalysisData;
  hypothesisName: string;
}

export const HypothesisView: React.FC<HypothesisViewProps> = ({ data, hypothesisName }) => {
  const { user } = useAuth();
  // Verdict styling based on assessment
  const getVerdictStyle = (verdict?: string) => {
    if (verdict?.includes('SUPPORTED') || verdict?.includes('Validated') || verdict?.includes('Strongly Validated')) {
      return {
        bg: 'bg-gradient-to-r from-green-50 to-emerald-50',
        border: 'border-green-500',
        text: 'text-green-900',
        icon: '✅'
      };
    }
    if (verdict?.includes('REFUTED')) {
      return {
        bg: 'bg-gradient-to-r from-red-50 to-pink-50',
        border: 'border-red-500',
        text: 'text-red-900',
        icon: '❌'
      };
    }
    if (verdict?.includes('MIXED')) {
      return {
        bg: 'bg-gradient-to-r from-yellow-50 to-orange-50',
        border: 'border-yellow-500',
        text: 'text-yellow-900',
        icon: '⚖️'
      };
    }
    return {
      bg: 'bg-gradient-to-r from-gray-50 to-slate-50',
      border: 'border-gray-500',
      text: 'text-gray-900',
      icon: '❓'
    };
  };

  const renderVerdict = () => {
    let verdict = data.hypothesis_verdict?.overall_assessment ||
                  data.hypothesis_verdict?.verdict ||
                  data.verdict?.overall_assessment ||  // Handle "verdict" object structure
                  (typeof data.executive_summary === 'object' ?
                   (data.executive_summary as ExecutiveSummary).verdict : null);

    // Extract verdict from executive_summary string if it contains verdict keywords
    if (!verdict && typeof data.executive_summary === 'string') {
      const summaryText = data.executive_summary.toLowerCase();
      if (summaryText.includes('conditionally validated') || summaryText.includes('conditionally supported')) {
        verdict = 'Conditionally Validated';
      } else if (summaryText.includes('validated') || summaryText.includes('supported')) {
        verdict = 'Validated';
      } else if (summaryText.includes('refuted') || summaryText.includes('not supported')) {
        verdict = 'Refuted';
      } else if (summaryText.includes('mixed') || summaryText.includes('partially')) {
        verdict = 'Mixed Evidence';
      }
    }

    if (!verdict) return null;

    const style = getVerdictStyle(verdict);
    const confidence = data.hypothesis_verdict?.confidence_level ||
                      data.verdict?.confidence_level ||  // Handle "verdict" object structure
                      (typeof data.executive_summary === 'object' ?
                       (data.executive_summary as ExecutiveSummary).confidence : null);

    return (
      <div className={`${style.bg} border-2 ${style.border} rounded-xl p-6 mb-6`}>
        <div className="flex items-start gap-4">
          <span className="text-3xl">{style.icon}</span>
          <div className="flex-1">
            <h2 className={`text-2xl font-bold ${style.text} mb-2`}>
              Verdict: {verdict}
            </h2>
            {confidence && (
              <div className="text-lg text-gray-700">
                Confidence Level: <span className="font-semibold">{confidence}</span>
              </div>
            )}
            {(data.hypothesis_verdict?.confidence_justification || data.verdict?.confidence_justification) && (
              <p className="mt-3 text-gray-600">
                {data.hypothesis_verdict?.confidence_justification || data.verdict?.confidence_justification}
              </p>
            )}

            {/* Conditions and Patterns - Collapsible within verdict */}
            {((data.hypothesis_verdict?.conditions_for_truth || data.verdict?.conditions_for_truth) ||
              (data.hypothesis_verdict?.key_evidence_patterns || data.verdict?.key_evidence_patterns)) && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <CollapsibleSection
                  title="View Conditions & Patterns"
                  icon=""
                  defaultOpen={false}
                  variant="minimal"
                  className="mb-0"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                    {(data.hypothesis_verdict?.conditions_for_truth || data.verdict?.conditions_for_truth) && (
                      <ConditionsForTruth conditions={(data.hypothesis_verdict?.conditions_for_truth || data.verdict?.conditions_for_truth) ?? []} />
                    )}
                    {(data.hypothesis_verdict?.key_evidence_patterns || data.verdict?.key_evidence_patterns) && (
                      <KeyEvidencePatterns patterns={(data.hypothesis_verdict?.key_evidence_patterns || data.verdict?.key_evidence_patterns) ?? []} />
                    )}
                  </div>
                </CollapsibleSection>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderExecutiveSummary = () => {
    if (!data.executive_summary) return null;

    if (typeof data.executive_summary === 'object') {
      const summary = data.executive_summary as ExecutiveSummary;
      return (
        <div className="bg-white rounded-xl p-6 shadow-md mb-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Executive Summary</h3>
          {summary.key_finding && (
            <p className="text-gray-700 leading-relaxed text-lg">
              {summary.key_finding}
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="bg-white rounded-xl p-6 shadow-md mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Executive Summary</h3>
        <p className="text-gray-700 leading-relaxed text-lg">{data.executive_summary}</p>
      </div>
    );
  };

  const formatTimestamp = (timestamp?: string | null) => {
    if (!timestamp) return null;
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {hypothesisName}
        </h1>
        <p className="text-lg text-gray-700">
          <strong>Statement:</strong> {data.statement || data.hypothesis_statement || data.hypothesis?.statement || 'Statement not available'}
        </p>
        {(data.analysis_timestamp || data.analysis_date || data.analyst) && (
          <div className="mt-2 text-sm text-gray-500">
            Analysis: {formatTimestamp(data.analysis_timestamp || data.analysis_date)}
            {data.analyst && <span> {data.analyst}</span>}
          </div>
        )}
      </div>

      {/* Not-yet-analysed notice */}
      {data.has_analysis === false && (
        <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-xl p-4 mb-6">
          <strong>Not yet analysed.</strong> No interviews have been ingested and analysed against this
          hypothesis yet. Below is the framework — the statement, the signals to look for, and the informing
          questions — so you can interrogate the hypothesis now.
        </div>
      )}

      {/* Framework signals (shown whenever present, e.g. before analysis) */}
      {data.signals && (
        ((data.signals.positive_signals?.length ?? 0) +
         (data.signals.negative_signals?.length ?? 0) +
         (data.signals.ambiguities?.length ?? 0) +
         (data.signals.expected_absences?.length ?? 0)) > 0 ? (
        <div className="bg-white rounded-xl p-6 shadow-md mb-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-1">Signals to look for</h3>
          <p className="text-sm text-gray-500 mb-4">
            Observable indicators the analysis matches against interview transcripts.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {(data.signals.positive_signals?.length ?? 0) > 0 && (
              <div className="border-l-4 border-green-400 bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">✅ Supports the hypothesis</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {data.signals.positive_signals!.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {(data.signals.negative_signals?.length ?? 0) > 0 && (
              <div className="border-l-4 border-red-400 bg-red-50 rounded-lg p-4">
                <h4 className="font-semibold text-red-900 mb-2">❌ Refutes the hypothesis</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {data.signals.negative_signals!.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {(data.signals.ambiguities?.length ?? 0) > 0 && (
              <div className="border-l-4 border-yellow-400 bg-yellow-50 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">⚖️ Ambiguous / mixed</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {data.signals.ambiguities!.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {(data.signals.expected_absences?.length ?? 0) > 0 && (
              <div className="border-l-4 border-gray-400 bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">🔇 Expected absences (telling silences)</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {data.signals.expected_absences!.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
        ) : null
      )}

      {/* 1. Verdict - Most Prominent */}
      {renderVerdict()}

      {/* 2. Executive Summary */}
      {renderExecutiveSummary()}

      {/* 3. Statistics */}
      {data.analysis_metadata && (
        <Statistics metadata={data.analysis_metadata} />
      )}

      {/* 4. Informing Questions - Collapsible */}
      {data.informing_questions && data.informing_questions.length > 0 && (
        <CollapsibleSection
          title={`Informing Questions (${data.informing_questions.length})`}
          icon=""
          defaultOpen={false}
          className="mb-6"
        >
          <div className="bg-white rounded-xl p-6">
            <div className="space-y-3">
              {data.informing_questions.map((question, idx) => (
                <div key={question.id || idx} className="border-l-4 border-purple-400 bg-purple-50 rounded-lg p-4">
                  <p className="text-gray-800 font-medium mb-2">{question.text}</p>
                  <div className="flex flex-wrap gap-2 text-sm">
                    {question.intent && (
                      <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                        Intent: {question.intent}
                      </span>
                    )}
                    {question.persona && (
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        Persona: {question.persona}
                      </span>
                    )}
                    {question.tags && question.tags.length > 0 && (
                      question.tags.map((tag, tagIdx) => (
                        <span key={tagIdx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                          {tag}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* 5. Full Evidence Data - Collapsible */}
      {data.evidence_items && data.evidence_items.length > 0 && (
        <CollapsibleSection
          title={`Evidence Analysis (${data.evidence_items.length} items)`}
          icon="📊"
          defaultOpen={false}
          className="mb-6"
        >
          <EvidenceViewer evidenceItems={data.evidence_items} />
        </CollapsibleSection>
      )}

      {/* 6. Signal Analysis - Collapsible */}
      {data.signal_analysis && (
        <CollapsibleSection
          title="Signal Analysis"
          icon="📡"
          defaultOpen={false}
        >
          <SignalAnalysis signals={data.signal_analysis} />
        </CollapsibleSection>
      )}

      {/* 7. Critical Evidence Examples - Collapsible */}
      {data.critical_evidence_examples && data.critical_evidence_examples.length > 0 && (
        <CollapsibleSection
          title="Critical Evidence Examples"
          icon="🔍"
          defaultOpen={false}
        >
          <EvidenceViewer evidenceItems={data.critical_evidence_examples} />
        </CollapsibleSection>
      )}

      {/* 8. Pattern Analysis - Collapsible */}
      {data.detailed_pattern_analysis && (
        <CollapsibleSection
          title="Pattern Analysis"
          icon="🔄"
          defaultOpen={false}
        >
          <div className="bg-white rounded-xl p-6">
            {data.detailed_pattern_analysis.convergent_evidence_patterns && (
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-3">Convergent Patterns</h4>
                {Array.isArray(data.detailed_pattern_analysis.convergent_evidence_patterns) ? (
                  data.detailed_pattern_analysis.convergent_evidence_patterns.map((pattern: any, idx: number) => (
                  <div key={idx} className="mb-3 p-4 bg-green-50 rounded-lg">
                    <div className="font-medium text-green-900">{pattern.pattern}</div>
                    <div className="text-sm text-gray-600 mt-1">{pattern.description}</div>
                    {pattern.evidence_strength && (
                      <div className="text-xs text-gray-500 mt-2">
                        Strength: {pattern.evidence_strength} | Frequency: {pattern.frequency || 'N/A'}
                      </div>
                    )}
                  </div>
                ))) : (
                  <p className="text-gray-600">{JSON.stringify(data.detailed_pattern_analysis.convergent_evidence_patterns)}</p>
                )}
              </div>
            )}

            {data.detailed_pattern_analysis.divergent_evidence_patterns && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Divergent Patterns</h4>
                {Array.isArray(data.detailed_pattern_analysis.divergent_evidence_patterns) ? (
                  data.detailed_pattern_analysis.divergent_evidence_patterns.map((pattern: any, idx: number) => (
                  <div key={idx} className="mb-3 p-4 bg-yellow-50 rounded-lg">
                    <div className="font-medium text-yellow-900">{pattern.pattern}</div>
                    <div className="text-sm text-gray-600 mt-1">{pattern.description}</div>
                  </div>
                ))) : (
                  <p className="text-gray-600">{JSON.stringify(data.detailed_pattern_analysis.divergent_evidence_patterns)}</p>
                )}
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* 9. Business Recommendations - Collapsible (hidden for staff) */}
      {data.business_recommendations && user?.role !== 'staff' && (
        <CollapsibleSection
          title="Business Recommendations"
          icon="💼"
          defaultOpen={false}
        >
          <Recommendations recommendations={data.business_recommendations} />
        </CollapsibleSection>
      )}

      {/* 10. Research Quality - Collapsible */}
      {data.research_quality_assessment && (
        <CollapsibleSection
          title="Research Quality Assessment"
          icon="📋"
          defaultOpen={false}
        >
          <div className="bg-white rounded-xl p-6">
            {data.research_quality_assessment.evidence_strength && (
              <div className="mb-4">
                <span className="font-medium text-gray-700">Evidence Strength:</span>
                <span className="ml-2 text-gray-600">{data.research_quality_assessment.evidence_strength}</span>
              </div>
            )}

            {data.research_quality_assessment.coverage_completeness && (
              <div className="mb-4">
                <span className="font-medium text-gray-700">Coverage:</span>
                <span className="ml-2 text-gray-600">{data.research_quality_assessment.coverage_completeness}</span>
              </div>
            )}

            {data.research_quality_assessment.coverage_gaps && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Coverage Gaps:</h4>
                {Array.isArray(data.research_quality_assessment.coverage_gaps) ? (
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    {data.research_quality_assessment.coverage_gaps.map((gap, idx) => (
                      <li key={idx}>{gap}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-600">{data.research_quality_assessment.coverage_gaps}</p>
                )}
              </div>
            )}

            {data.research_quality_assessment.confidence_limiters && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Confidence Limiters:</h4>
                {Array.isArray(data.research_quality_assessment.confidence_limiters) ? (
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    {data.research_quality_assessment.confidence_limiters.map((limiter, idx) => (
                      <li key={idx}>{limiter}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-600">{data.research_quality_assessment.confidence_limiters}</p>
                )}
              </div>
            )}

            {(data.research_quality_assessment.methodology_strengths ||
              data.research_quality_assessment.methodological_strengths ||
              data.research_quality_assessment.strengths) && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Strengths:</h4>
                {(() => {
                  const strengths = data.research_quality_assessment.methodology_strengths ||
                                   data.research_quality_assessment.methodological_strengths ||
                                   data.research_quality_assessment.strengths;
                  return Array.isArray(strengths) ? (
                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                      {strengths.map((strength, idx) => (
                        <li key={idx}>{strength}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-600">{strengths}</p>
                  );
                })()}
              </div>
            )}

            {data.research_quality_assessment.limitations && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Limitations:</h4>
                {Array.isArray(data.research_quality_assessment.limitations) ? (
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    {data.research_quality_assessment.limitations.map((limitation, idx) => (
                      <li key={idx}>{limitation}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-600">{data.research_quality_assessment.limitations}</p>
                )}
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* 11. Additional Verdict Details - Collapsible */}
      {data.hypothesis_verdict && (data.hypothesis_verdict.reasoning ||
        (data.hypothesis_verdict.overall_assessment &&
         !data.hypothesis_verdict.conditions_for_truth &&
         !data.hypothesis_verdict.key_evidence_patterns)) && (
        <CollapsibleSection
          title="Additional Verdict Details"
          icon="ℹ️"
          defaultOpen={false}
        >
          <div className="bg-white rounded-xl p-6">
            {data.hypothesis_verdict.reasoning && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Reasoning:</h4>
                <p className="text-gray-600">{data.hypothesis_verdict.reasoning}</p>
              </div>
            )}

            {data.hypothesis_verdict.overall_assessment && (
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Assessment Details:</h4>
                <p className="text-gray-600">{data.hypothesis_verdict.overall_assessment}</p>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Conclusion */}
      {data.conclusion && (
        <CollapsibleSection
          title="Conclusion"
          icon="📝"
          defaultOpen={false}
        >
          <div className="bg-gray-50 rounded-xl p-6">
            {typeof data.conclusion === 'object' ? (
              <>
                {data.conclusion.synthesis && (
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-700 mb-2">Synthesis</h4>
                    <p className="text-gray-600">{data.conclusion.synthesis}</p>
                  </div>
                )}
                {data.conclusion.strategic_implications && (
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-700 mb-2">Strategic Implications</h4>
                    {Array.isArray(data.conclusion.strategic_implications) ? (
                      <ul className="list-disc list-inside space-y-1 text-gray-600">
                        {data.conclusion.strategic_implications.map((imp: string, idx: number) => (
                          <li key={idx}>{imp}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-600">{data.conclusion.strategic_implications}</p>
                    )}
                  </div>
                )}
                {data.conclusion.execution_priority && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Execution Priority</h4>
                    <p className="text-gray-600">{data.conclusion.execution_priority}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-600">{data.conclusion}</p>
            )}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
};
